import { useState } from 'react';
import { useAllMarketplacesQuery } from '@anytag/graphql';
import { NOT_FOUND } from '@anytag/shared-constants';
import { Header, ListWithPagination } from '@anytag/shared-molecules';
import { useMarketplacePermissions, useUserRoles } from '@anytag/auth';
import { ButtonLink, RenderDataWithFallback } from '@anytag/shared-atoms';
import { getOffset, getValuableVariables } from '@anytag/shared-utils';
import { useQueryHelper, useUpdateDocumentTitle } from '@anytag/shared-hooks';
import {
  MarketplaceOnboarding,
  MarketplaceCampaignsList,
  MarketplaceCampaignsFilter,
  useMarketplaceCampaignsFilter
} from '@anytag/shared-organisms';

export const Marketplaces = () => {
  useUpdateDocumentTitle({ title: 'documentTitle.Marketplace', appHeader: 'appHeader.Marketplace' });
  const { isAdvertiser } = useUserRoles();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { hideAddMarketplaceBtn } = useMarketplacePermissions();
  const { page, limit, filter } = useMarketplaceCampaignsFilter();
  const [isOnboarding, setIsOnboarding] = useState(false);
  const { data, previousData, loading } = useAllMarketplacesQuery({
    fetchPolicy: 'no-cache', // will always trigger onError to setIsOnboarding
    variables: getValuableVariables({
      ...filter,
      limit,
      offset: getOffset(page, limit),
      status: filter.status || null,
      campaignDetailType: filter.campaignDetailType || null
    }),
    onError: (error) => {
      if (error.message === NOT_FOUND) {
        setIsOnboarding(true);
      } else {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      }
    }
  });

  const result = loading ? previousData : data;
  const totalRecords = result?.allMarketplacesCount?.totalNumber;

  return (
    <RenderDataWithFallback
      hasNoData={isOnboarding}
      loading={isAdvertiser && loading}
      noDataNode={<MarketplaceOnboarding />}
    >
      <ListWithPagination pagination={{ totalRecords }}>
        <Header>
          <ButtonLink
            className="ml-auto min-w-[143px]"
            variant="outline"
            to="/marketplace/reorder"
            title={t('Button.Reorder Campaigns')}
            search={{ filter: { countryId: filter.countryId } }}
          />

          {!hideAddMarketplaceBtn ? (
            <ButtonLink
              variant="solid"
              to="/marketplace/add"
              className="min-w-[143px]"
              title={t('Button.Add Campaign')}
              prefixIcon={{ icon: 'plus', size: '10px' }}
            />
          ) : null}
        </Header>
        <MarketplaceCampaignsFilter />
        <MarketplaceCampaignsList loading={loading} totalRecords={totalRecords} data={result?.allMarketplaces || []} />
      </ListWithPagination>
    </RenderDataWithFallback>
  );
};
